import {ExtractionStrategy, Script, Scripts, STRATEGY_NAMES, StrategyKind} from '@/api/models/Scripts.ts';
import ConfirmDeleteDialog, {ConfirmFn} from '@/components/ConfirmDeleteDialog.tsx';
import CopyButton from '@/components/CopyButton.tsx';
import NoSearchResults from '@/components/NoSearchResults.tsx';
import Pending from '@/components/Pending.tsx';
import Search from '@/components/Search.tsx';
import TitleWithDropdown from '@/components/TitleWithDropdown.tsx';
import {ToastErrors} from '@/toaster.ts';
import {Button, Code, HTMLTable, Icon, MenuItem, NonIdealState, Tooltip} from '@blueprintjs/core';
import {ReactElement, ReactNode, useCallback, useMemo, useState} from 'react';
import {Link} from 'react-router';

export default function ScriptsPage(): ReactElement {
	const [sequence, setSequence] = useState(0);
	const scripts = useMemo(() => {
		return Scripts.list();
	}, [sequence]);

	const [filter, setFilter] = useState('');

	const onRefresh = useCallback(() => {
		setSequence(seq => seq + 1);
	}, []);

	return (
		<>
			<TitleWithDropdown title="Scripts" rightElement={<Search onSearch={setFilter} />}>
				<MenuItem icon="refresh" text="Refresh" onClick={onRefresh} />

				<Link to="/scripts/new" className="plain-link">
					<MenuItem icon="add" text="New Script" tagName="span" />
				</Link>
			</TitleWithDropdown>

			<Pending resource={scripts}>
				{scripts => <List filter={filter} refresh={onRefresh} scripts={scripts} />}
			</Pending>
		</>
	);
}

interface Props {
	scripts: Script[],
	filter: string,
	refresh: () => void,
}

function List({scripts, filter, refresh}: Props): ReactElement {
	const filtered = useMemo(() => {
		if (filter.length === 0)
			return scripts;

		return scripts.filter(v => v.feed.name.toLocaleLowerCase().includes(filter));
	}, [scripts, filter]);

	const [deleteTarget, setDeleteTarget] = useState<Script | null>(null);

	const onDeleteDialogClose = useCallback(() => {
		setDeleteTarget(null);
	}, []);

	const onDeleteDialogConfirm: ConfirmFn = useCallback(async () => {
		if (!deleteTarget)
			throw new Error('onDeleteDialogConfirm called without a deleteTarget set');

		try {
			await Scripts.remove(deleteTarget.id);
		} catch (error) {
			ToastErrors.fromError(error);
			return;
		}

		setDeleteTarget(null);
		refresh();
	}, [deleteTarget, refresh]);

	if (scripts.length === 0) {
		return (
			<NonIdealState
				icon="wind"
				title={(
					<>Does anybody have a <s>map</s> script?</>
				)}
				description={(
					<>
						You currently have no scripts.<br />
						Use the button below to create one!
					</>
				)}
				action={(
					<Link to="/scripts/new">
						<Button icon="add" text="Create Script" variant="outlined" intent="primary" />
					</Link>
				)}
			/>
		);
	} else if (filtered.length === 0)
		return <NoSearchResults />;

	return (
		<>
			<HTMLTable striped={true} interactive={true} style={{width: '100%'}}>
				<thead>
					<tr>
						<th>ID.</th>
						<th>Feed</th>
						<th>VIN Strategy</th>
						<th>&nbsp;</th>
					</tr>
				</thead>

				<tbody>
					{filtered.map(v => <Row key={v.id} script={v} onDelete={setDeleteTarget} />)}
				</tbody>
			</HTMLTable>

			{deleteTarget && (
				<ConfirmDeleteDialog
					confirmText={deleteTarget.feed.name}
					onClose={onDeleteDialogClose}
					onConfirm={onDeleteDialogConfirm}
					isOpen={true}
				/>
			)}
		</>
	);
}

type DeleteFn = (script: Script) => void;

interface RowProps {
	script: Script,
	onDelete: DeleteFn,
}

function Row({script, onDelete}: RowProps): ReactElement {
	const onDeleteClick = useCallback(() => {
		onDelete(script);
	}, [onDelete, script]);

	const copyText = useMemo(() => {
		return `<script src="${process.env.VEHICLE_SCRIPT_URL}" data-config-id="${script.id}"></script>`;
	}, [script]);

	return (
		<tr>
			<td>{script.id}</td>
			<td>
				<Link to={`/feeds/${script.feed.id}`}>
					{script.feed.name} <Icon style={{marginLeft: 5}} icon="share" />
				</Link>
			</td>
			<td>{getStrategyText(script.vinExtractionStrategy)}</td>
			<td style={{display: 'flex', justifyContent: 'end', gap: 10}}>
				<Tooltip content="Edit">
					<Link to={`/scripts/${script.id}`}>
						<Button icon="edit" variant="minimal" size="small" />
					</Link>
				</Tooltip>

				<CopyButton copyText={copyText} tooltip="Copy Embed Code" />

				<Tooltip content="Delete">
					<Button icon="delete" intent="warning" variant="minimal" size="small" onClick={onDeleteClick} />
				</Tooltip>
			</td>
		</tr>
	);
}

function getStrategyText(strategy: ExtractionStrategy): ReactNode {
	const name = STRATEGY_NAMES[strategy.kind];

	switch (strategy.kind) {
		case StrategyKind.CssSelector:
			return <>{name} (<Code>{strategy.selector}</Code>)</>;

		default:
			return name;
	}
}
